import { faAppStoreIos, faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Store, Message } from '@mui/icons-material';

export function calculateAverageRating(reviews: any[]) {
  if (reviews.length === 0) return 0; // Avoid division by zero

  const totalRating = reviews.reduce((sum, review) => sum + parseFloat(review.rating), 0);

  return (totalRating / reviews.length).toFixed(2); // Round to 2 decimal places
}

export function timeSince(date: string) {
  if (date === undefined || date === null) return "-";

  var seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);

  var interval = seconds / 31536000;

  if (interval > 1) {
    return Math.floor(interval) + " years ago";
  }

  interval = seconds / 2592000;
  if (interval > 1) {
    return Math.floor(interval) + " months ago";
  }

  interval = seconds / 86400;
  if (interval > 1) {
    return Math.floor(interval) + " days ago";
  }

  interval = seconds / 3600;
  if (interval > 1) {
    return Math.floor(interval) + " hours ago";
  }

  interval = seconds / 60;
  if (interval > 1) {
    return Math.floor(interval) + " minutes ago";
  }

  return Math.floor(seconds) + " seconds ago";
}

export function iconFor(store: string, size: 18 | 24 = 24) {
  const huaweiIcon = <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width={size} height={size} viewBox="0 0 48 48">
    <path fill="#e53935" d="M15,6h18c4.971,0,9,4.029,9,9v18c0,4.971-4.029,9-9,9H15c-4.971,0-9-4.029-9-9V15 C6,10.029,10.029,6,15,6z"></path><path fill="#fff" d="M21.775,20.935c-1.506,0-2.669,1.312-2.669,3.716c0,1.571,2.378,5.599,4.486,9.127 C23.592,24.644,23.368,23.655,21.775,20.935z"></path><path fill="#fff" d="M26.225,20.935c-1.624,2.674-1.817,3.711-1.817,12.846c2.108-3.528,4.486-7.558,4.486-9.13 C28.894,22.245,27.731,20.935,26.225,20.935z"></path><path fill="#fff" d="M16.581,23.904c-2.21,1.543-1.484,5.192-0.369,6.386c0.735,0.771,3.596,2.159,6.305,4 L16.581,23.904z"></path><path fill="#fff" d="M31.419,23.904l-5.936,10.385c2.709-1.84,5.57-3.229,6.305-4 C32.903,29.096,33.629,25.447,31.419,23.904z"></path><path fill="#fff" d="M13.612,29.84c0.036,4.111,2.545,5.191,4.241,5.191h4.241L13.612,29.84z"></path><path fill="#fff" d="M34.388,29.84l-8.483,5.191h4.241C31.843,35.031,34.352,33.951,34.388,29.84z"></path><path fill="#fff" d="M15.926,35.775C16.163,37.002,16.698,38,18.239,38c1.542,0,3.035-0.889,3.855-2.225H15.926z"></path><path fill="#fff" d="M25.905,35.775C26.726,37.111,28.219,38,29.761,38s2.076-0.998,2.314-2.225H25.905z"></path><circle cx="14.5" cy="10.5" r="1.5" fill="#b71c1c"></circle><circle cx="33.5" cy="10.5" r="1.5" fill="#b71c1c"></circle><path fill="none" stroke="#fff" stroke-linecap="round" stroke-miterlimit="10" d="M33.5,10.5c0,0-2,5-9.5,5s-9.5-5-9.5-5"></path>
  </svg>

  switch (store) {
    case "playstore":
      return <FontAwesomeIcon icon={faGooglePlay} />;
    case "googlestore":
      return <FontAwesomeIcon icon={faGooglePlay} />;
    case "applestore":
      return <FontAwesomeIcon icon={faAppStoreIos} />;
    case "appgallery":
      return <>{huaweiIcon}</>;
    case "profeed":
      return <Message />;
    default:
      return <Store />;
  };
}

export function canAddMoreApps(plan: string, apps: number) {
  if (plan === 'A') {
    return apps < 1;
  }

  if (plan === 'AA') {
    return apps <= 2;
  }

  if (plan === 'AAA') {
    return apps <= 5;
  }

  if (plan === 'U') {
    return apps <= 20;
  }
}

export function base64For(file: any) {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = function () {
      resolve(reader.result);
    };

    reader.onerror = function (error) {
      reject(error);
    }

  });
}

export function getInitials(name: string): string {
  // Divide o nome em partes (usando espaços como delimitadores)
  const parts: string[] = name.trim().split(' ');

  // Se houver apenas um nome, retorna a inicial dele
  if (parts.length === 1) {
    return parts[0][0].toUpperCase();
  }

  // Pega a primeira e a última parte e retorna as iniciais em maiúsculas
  const firstInitial: string = parts[0][0].toUpperCase();
  const lastInitial: string = parts[parts.length - 1][0].toUpperCase();

  return firstInitial + lastInitial;
}

export function formatFriendlyDate(isoDate: string, locale: string): string {
  const date = new Date(isoDate);

  // Format the date to a more user-friendly format
  return date.toLocaleDateString(locale ?? "en-US", {
    year: "numeric", // e.g., "2025"
    month: "long",   // e.g., "September"
    day: "numeric"   // e.g., "22"
  });
}

export const planA = {
  name: 'Solo',
  description: 'Everything you need to understand your app.',
  priceMonth: '19',
  priceYear: '182',
  code: 'A',
  features: [
  ]
};

export const planAA = {
  name: 'Starter',
  description: 'For teams that need to stay on top of your app feedback.',
  priceMonth: '48',
  priceYear: '460',
  code: 'AA',
  features: [
  ]
};

export const planAAA = {
  name: 'Growth',
  description: 'For teams ready to act on feedback.',
  priceMonth: '99',
  priceYear: '960',
  code: 'AAA',
  features: [
  ]
};

export const planU = {
  name: 'Premium',
  code: 'U',
  description: 'For teams that need feedback beyond app store reviews.',
  features: [
  ]
};

export const parseKeyValue = (jsonString: string): { key: string; value: string } | null => {
  try {
    const parsedObject = JSON.parse(jsonString); // Parse the JSON string
    const [key, value] = Object.entries(parsedObject)[0] as [string, string]; // Extract key-value pair
    return { key, value };
  } catch (error) {
    console.error('Failed to parse JSON string:', error);
    return null; // Return null if parsing fails
  }
};

export const getLanguageName = (isoCode: string) => {
  return new Intl.DisplayNames([navigator.language], { type: "language" }).of(isoCode) || isoCode;
};

export const getCountryName = (territoryCode: string) => {
  // Mapping de códigos ISO 3 letras para 2 letras para compatibilidade
  const iso3To2: { [key: string]: string } = {
    'ABW': 'AW', 'AFG': 'AF', 'AGO': 'AO', 'AIA': 'AI', 'ALA': 'AX', 'ALB': 'AL', 'AND': 'AD', 'ARE': 'AE',
    'ARG': 'AR', 'ARM': 'AM', 'ASM': 'AS', 'ATA': 'AQ', 'ATF': 'TF', 'ATG': 'AG', 'AUS': 'AU', 'AUT': 'AT',
    'AZE': 'AZ', 'BDI': 'BI', 'BEL': 'BE', 'BEN': 'BJ', 'BES': 'BQ', 'BFA': 'BF', 'BGD': 'BD', 'BGR': 'BG',
    'BHR': 'BH', 'BHS': 'BS', 'BIH': 'BA', 'BLM': 'BL', 'BLR': 'BY', 'BLZ': 'BZ', 'BMU': 'BM', 'BOL': 'BO',
    'BRA': 'BR', 'BRB': 'BB', 'BRN': 'BN', 'BTN': 'BT', 'BVT': 'BV', 'BWA': 'BW', 'CAF': 'CF', 'CAN': 'CA',
    'CCK': 'CC', 'CHE': 'CH', 'CHL': 'CL', 'CHN': 'CN', 'CIV': 'CI', 'CMR': 'CM', 'COD': 'CD', 'COG': 'CG',
    'COK': 'CK', 'COL': 'CO', 'COM': 'KM', 'CPV': 'CV', 'CRI': 'CR', 'CUB': 'CU', 'CUW': 'CW', 'CXR': 'CX',
    'CYM': 'KY', 'CYP': 'CY', 'CZE': 'CZ', 'DEU': 'DE', 'DJI': 'DJ', 'DMA': 'DM', 'DNK': 'DK', 'DOM': 'DO',
    'DZA': 'DZ', 'ECU': 'EC', 'EGY': 'EG', 'ERI': 'ER', 'ESH': 'EH', 'ESP': 'ES', 'EST': 'EE', 'ETH': 'ET',
    'FIN': 'FI', 'FJI': 'FJ', 'FLK': 'FK', 'FRA': 'FR', 'FRO': 'FO', 'FSM': 'FM', 'GAB': 'GA', 'GBR': 'GB',
    'GEO': 'GE', 'GGY': 'GG', 'GHA': 'GH', 'GIB': 'GI', 'GIN': 'GN', 'GLP': 'GP', 'GMB': 'GM', 'GNB': 'GW',
    'GNQ': 'GQ', 'GRC': 'GR', 'GRD': 'GD', 'GRL': 'GL', 'GTM': 'GT', 'GUF': 'GF', 'GUM': 'GU', 'GUY': 'GY',
    'HKG': 'HK', 'HMD': 'HM', 'HND': 'HN', 'HRV': 'HR', 'HTI': 'HT', 'HUN': 'HU', 'IDN': 'ID', 'IMN': 'IM',
    'IND': 'IN', 'IOT': 'IO', 'IRL': 'IE', 'IRN': 'IR', 'IRQ': 'IQ', 'ISL': 'IS', 'ISR': 'IL', 'ITA': 'IT',
    'JAM': 'JM', 'JEY': 'JE', 'JOR': 'JO', 'JPN': 'JP', 'KAZ': 'KZ', 'KEN': 'KE', 'KGZ': 'KG', 'KHM': 'KH',
    'KIR': 'KI', 'KNA': 'KN', 'KOR': 'KR', 'KWT': 'KW', 'LAO': 'LA', 'LBN': 'LB', 'LBR': 'LR', 'LBY': 'LY',
    'LCA': 'LC', 'LIE': 'LI', 'LKA': 'LK', 'LSO': 'LS', 'LTU': 'LT', 'LUX': 'LU', 'LVA': 'LV', 'MAC': 'MO',
    'MAF': 'MF', 'MAR': 'MA', 'MCO': 'MC', 'MDA': 'MD', 'MDG': 'MG', 'MDV': 'MV', 'MEX': 'MX', 'MHL': 'MH',
    'MKD': 'MK', 'MLI': 'ML', 'MLT': 'MT', 'MMR': 'MM', 'MNE': 'ME', 'MNG': 'MN', 'MNP': 'MP', 'MOZ': 'MZ',
    'MRT': 'MR', 'MSR': 'MS', 'MTQ': 'MQ', 'MUS': 'MU', 'MWI': 'MW', 'MYS': 'MY', 'MYT': 'YT', 'NAM': 'NA',
    'NCL': 'NC', 'NER': 'NE', 'NFK': 'NF', 'NGA': 'NG', 'NIC': 'NI', 'NIU': 'NU', 'NLD': 'NL', 'NOR': 'NO',
    'NPL': 'NP', 'NRU': 'NR', 'NZL': 'NZ', 'OMN': 'OM', 'PAK': 'PK', 'PAN': 'PA', 'PCN': 'PN', 'PER': 'PE',
    'PHL': 'PH', 'PLW': 'PW', 'PNG': 'PG', 'POL': 'PL', 'PRI': 'PR', 'PRK': 'KP', 'PRT': 'PT', 'PRY': 'PY',
    'PSE': 'PS', 'PYF': 'PF', 'QAT': 'QA', 'REU': 'RE', 'ROU': 'RO', 'RUS': 'RU', 'RWA': 'RW', 'SAU': 'SA',
    'SDN': 'SD', 'SEN': 'SN', 'SGP': 'SG', 'SGS': 'GS', 'SHN': 'SH', 'SJM': 'SJ', 'SLB': 'SB', 'SLE': 'SL',
    'SLV': 'SV', 'SMR': 'SM', 'SOM': 'SO', 'SPM': 'PM', 'SRB': 'RS', 'SSD': 'SS', 'STP': 'ST', 'SUR': 'SR',
    'SVK': 'SK', 'SVN': 'SI', 'SWE': 'SE', 'SWZ': 'SZ', 'SXM': 'SX', 'SYC': 'SC', 'SYR': 'SY', 'TCA': 'TC',
    'TCD': 'TD', 'TGO': 'TG', 'THA': 'TH', 'TJK': 'TJ', 'TKL': 'TK', 'TKM': 'TM', 'TLS': 'TL', 'TON': 'TO',
    'TTO': 'TT', 'TUN': 'TN', 'TUR': 'TR', 'TUV': 'TV', 'TWN': 'TW', 'TZA': 'TZ', 'UGA': 'UG', 'UKR': 'UA',
    'UMI': 'UM', 'URY': 'UY', 'USA': 'US', 'UZB': 'UZ', 'VAT': 'VA', 'VCT': 'VC', 'VEN': 'VE', 'VGB': 'VG',
    'VIR': 'VI', 'VNM': 'VN', 'VUT': 'VU', 'WLF': 'WF', 'WSM': 'WS', 'YEM': 'YE', 'ZAF': 'ZA', 'ZMB': 'ZM',
    'ZWE': 'ZW'
  };

  try {
    // Se o código tem 3 caracteres, tenta converter para código de 2 caracteres
    const code = territoryCode.length === 3 ?
      iso3To2[territoryCode.toUpperCase()] || territoryCode :
      territoryCode;

    // Usa a API Intl.DisplayNames para obter o nome do país
    return new Intl.DisplayNames([navigator.language], { type: "region" }).of(code.toUpperCase()) || territoryCode;
  } catch (error) {
    // Se houver erro, retorna o código original
    return territoryCode;
  }
};